import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import Loader from 'components/Loader';
import { ClientSubscriptions } from 'components/WebsocketSubscriptions';
import EventDetail from './EventDetail';
import {
	setCurrentEvent,
	setClient,
	setCurrentClientID,
	thunkFetchEvent,
	thunkFetchAllClients,
} from '@/redux/actions';
import { isValidEventAdmin, hasSomeRoleInEvent } from '../../redux/util';
import { EVENT_ADMIN_CONTRIBUTOR_TYPES } from 'enums';
import { useParams } from 'react-router-dom';
import logger from '@/logger';

const EventDetailContainer = ({
	setCurrentEvent,
	setClient,
	setCurrentClientID,
	currentClient,
	fetchEvent,
	fetchAllClients,
	admins,
	adminSSOProfile,
	clients,
	currentEvent,
}) => {
	const [roles, setRoles] = useState([]);
	const { eventID } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		fetchAllClients('EventDetailContainer mount useEffect');

		return () => {
			Sentry.setTag('event_id', null);
			Sentry.setTag('client_id', null);
		};
	}, []);

	useEffect(() => {
		fetchEvent(eventID, 'EventDetailContainer eventID useEffect');
		setCurrentEvent(eventID);
	}, [eventID]);

	useEffect(() => {
		if (currentEvent && currentEvent.id) {
			Sentry.setTag('event_id', currentEvent.id);
			Sentry.setTag('client_id', currentEvent.client_id);

			if (currentEvent.deleted_at) {
				// we should never be in here - the event will just not be returned
				logger.info('Event was deleted - navigating to events', {
					location: 'EventDetailContainer.useEffect',
				});
				navigate(`/events`);
			}
			const getRolesForEvent = event => {
				const adminPermissions = admins[adminSSOProfile.email];
				const newRoles = Object.keys(EVENT_ADMIN_CONTRIBUTOR_TYPES);
				if (isValidEventAdmin(event)) return newRoles;
				if (!adminPermissions) return [];
				return adminPermissions.perms
					.filter(perm => perm.event.id === parseInt(event.id))
					.map(perm => perm.permission);
			};
			const rolesForEvent = getRolesForEvent(currentEvent);
			if (!hasSomeRoleInEvent(currentEvent) && rolesForEvent.length === 0) {
				logger.warn('No roles for event - navigating to events', {
					location: 'EventDetailContainer.useEffect',
				});
				navigate(`/events`);
			}

			setRoles(rolesForEvent);
		}
	}, [currentEvent]);

	useEffect(() => {
		if (
			currentEvent &&
			currentEvent.client_id &&
			currentClient &&
			currentClient.id &&
			currentEvent.client_id !== currentClient.id
		) {
			// Expected, self-healing condition: the event in the URL belongs to a
			// different client than the currently-active one (e.g. deep link, refresh,
			// or bookmark). We switch the active client to match below, so this is a
			// breadcrumb (warn), not a captured exception (error).
			logger.warn(
				`Event ID ${eventID} belongs to client ${currentEvent.client_id} but active client is ${currentClient.id} - switching active client`,
				{
					location: 'EventDetailContainer.useEffect',
				}
			);
			const newClientID = currentEvent.client_id;
			if (clients[newClientID]) {
				logger.info('Setting new ClientID', {
					location: 'EventDetailContainer.useEffect',
					newClientID,
				});
				setCurrentClientID(newClientID);
				setClient(clients[newClientID], 'EventDetail - client/event missmatch');
			} else {
				logger.info('Client not in clients - navigating to events', {
					location: 'EventDetailContainer.useEffect',
					clients,
				});
				navigate(`/events`);
			}
		}
	}, [currentEvent, currentClient]);

	const refetch = () => fetchEvent(eventID, 'EventDetail - refetch');

	if (!currentEvent || !currentClient) {
		return <Loader />;
	}

	return (
		<ClientSubscriptions>
			<EventDetail
				event={currentEvent}
				client={currentClient}
				roles={roles}
				refetch={refetch}
				adminSSOProfile={adminSSOProfile}
			/>
		</ClientSubscriptions>
	);
};

const msp = ({ admins, clients, events, auth }) => ({
	adminSSOProfile: auth.adminSSOProfile,
	admins,
	clients: clients.clients,
	currentClient: clients.currentClient,
	currentEvent: events.currentEvent,
});

const mdp = dispatch => ({
	setCurrentEvent: client => dispatch(setCurrentEvent(client)),
	setClient: (client, from) => dispatch(setClient(client, from)),
	setCurrentClientID: clientID => dispatch(setCurrentClientID(clientID)),
	fetchEvent: (eventID, from) => dispatch(thunkFetchEvent(eventID, from)),
	fetchAllClients: from => dispatch(thunkFetchAllClients(from)),
});

export default connect(msp, mdp)(EventDetailContainer);
